import {
  formatKey,
  isKeyEstimated,
  sameContent,
  shapeKeyOf,
  soundingKeyOf,
  type Notation,
  type RevisionContent,
} from '../../domain'
import common from '../common.module.css'
import { t } from '../strings'
import styles from './Play.module.css'

interface TunePanelProps {
  /** La ficha guardada. */
  base: RevisionContent
  /** La ficha tal como se ve ahora, con la transposición y la cejilla aplicadas. */
  view: RevisionContent
  shift: number
  notation: Notation
  saving: boolean
  onShift: (shift: number) => void
  onCapo: (capo: number) => void
  onReset: () => void
  onSave: () => void
}

/**
 * Tono y cejilla. Transponer cambia el sonido (las formas y el tono suben o bajan lo mismo);
 * la cejilla cambia la forma de los dedos sin cambiar el sonido (§3.3). Lo que se ve aquí es una
 * vista: no se guarda hasta que lo pides, y entonces se crea una revisión nueva.
 */
export function TunePanel({
  base,
  view,
  shift,
  notation,
  saving,
  onShift,
  onCapo,
  onReset,
  onSave,
}: TunePanelProps) {
  const changed = !sameContent(base, view)
  const shape = shapeKeyOf(view)
  const sounding = soundingKeyOf(view)
  const show = (key: NonNullable<typeof shape>) => formatKey(key, notation, { unicode: true })
  const p = t.play

  return (
    <div>
      <h2>{p.tuneTitle}</h2>
      <div className={styles.group}>
        <span className={common.muted}>{p.transpose}</span>
        <button
          aria-label={`${p.transpose} −1`}
          disabled={shift <= -11}
          onClick={() => onShift(shift - 1)}
        >
          −
        </button>
        <span className={styles.stepValue}>{p.semitones(shift)}</span>
        <button
          aria-label={`${p.transpose} +1`}
          disabled={shift >= 11}
          onClick={() => onShift(shift + 1)}
        >
          +
        </button>
      </div>
      <div className={styles.group} style={{ marginTop: 6 }}>
        <span className={common.muted}>{p.capoLabel}</span>
        <button
          aria-label={`${p.capoLabel} −1`}
          disabled={view.capo <= 0}
          onClick={() => onCapo(view.capo - 1)}
        >
          −
        </button>
        <span className={styles.stepValue}>{view.capo === 0 ? p.capoNone : view.capo}</span>
        <button
          aria-label={`${p.capoLabel} +1`}
          disabled={view.capo >= 12}
          onClick={() => onCapo(view.capo + 1)}
        >
          +
        </button>
      </div>
      <p className={styles.hint}>
        {shape && (isKeyEstimated(base) ? p.keyEstimated(show(shape)) : p.key(show(shape)))}
        {sounding && view.capo > 0 && ` · ${p.sounding(show(sounding))}`}
      </p>
      <div className={styles.group} style={{ marginTop: 6 }}>
        <button disabled={!changed} onClick={onReset}>
          {p.reset}
        </button>
        <button className="primary" disabled={!changed || saving} onClick={onSave}>
          {p.saveTune}
        </button>
      </div>
      <p className={styles.hint}>{p.tuneHint}</p>
    </div>
  )
}
